// Plan 23b, Task 3.2 — Scoreboard Row primitive
// Composes HP Bar (Plan 23a) + entrance animation (Plan 20a toast-scoreboard-slide hook).

import type { UIComponentHandle, UIComponentConfig } from './types';
import { createHPBar } from './hp-bar';
import { createToastScoreboardSlideHook } from '../vfx/toast-scoreboard-slide';
import type { GsapTimelineHandle } from '../../renderer/vfx/gsap-registry';

// ─────────────────────────────────────────────────────────────────────────────
// Tokens (from docs/design/tokens.json)
// ─────────────────────────────────────────────────────────────────────────────
const TOKEN = {
  /** color.ui.panel-bg-alpha */
  PANEL_BG_ALPHA: 'rgba(30, 35, 50, 0.82)',
  /** border-radius.md */
  BORDER_RADIUS_MD: '8px',
  /** border-radius.circle */
  BORDER_RADIUS_CIRCLE: '50%',
  /** spacing.sm */
  SPACING_SM: '8px',
  /** spacing.md */
  SPACING_MD: '16px',
  /** spacing.xs */
  SPACING_XS: '4px',
  /** color.placement.first */
  PLACEMENT_FIRST: '#FFD700',
  /** color.placement.second */
  PLACEMENT_SECOND: '#C0C0C0',
  /** color.placement.third */
  PLACEMENT_THIRD: '#CD7F32',
  /** color.placement.other */
  PLACEMENT_OTHER: '#707888',
  /** shadow.glow-amber (top-level shadow key) */
  SHADOW_GLOW_AMBER: '0 0 12px rgba(232,160,32,0.50)',
  /** typography.family.body */
  FONT_BODY: "'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif",
  /** typography.weight.bold */
  FONT_WEIGHT_BOLD: 700,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export interface ScoreboardRowOpts {
  playerName: string;
  playerColor: string; // hex from color.player.*
  avatarUrl?: string;
  currentHP: number;
  maxHP: number;
  keys: number;
  trophies: number;
  rank: number; // 1-based
}

export interface ScoreboardRowHandle extends UIComponentHandle {
  setRank(rank: number): void;
  setHP(current: number, max: number): void;
  setKeys(n: number): void;
  setTrophies(n: number): void;
  setEliminated(b: boolean): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Return border-left CSS value for a given rank. */
function rankBorderLeft(rank: number): string {
  let color: string;
  if (rank === 1) color = TOKEN.PLACEMENT_FIRST;
  else if (rank === 2) color = TOKEN.PLACEMENT_SECOND;
  else if (rank === 3) color = TOKEN.PLACEMENT_THIRD;
  else color = TOKEN.PLACEMENT_OTHER;
  return `3px solid ${color}`;
}

/** Return box-shadow for a given rank (only rank 1 gets glow). */
function rankBoxShadow(rank: number): string {
  return rank === 1 ? TOKEN.SHADOW_GLOW_AMBER : 'none';
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createScoreboardRow(
  config: UIComponentConfig & ScoreboardRowOpts,
): ScoreboardRowHandle {
  const {
    gsapRegistry,
    mount,
    playerName,
    playerColor,
    avatarUrl,
    currentHP,
    maxHP,
    keys,
    trophies,
    rank,
  } = config;

  let disposed = false;

  // ── Build DOM ──────────────────────────────────────────────────────────────

  // Row container
  const row = document.createElement('div');
  row.className = 'ui-scoreboard-row';
  row.style.cssText = [
    `background-color: ${TOKEN.PANEL_BG_ALPHA}`,
    `border-radius: ${TOKEN.BORDER_RADIUS_MD}`,
    `padding: ${TOKEN.SPACING_SM} ${TOKEN.SPACING_MD}`,
    `height: 56px`,
    `margin-bottom: ${TOKEN.SPACING_XS}`,
    `display: flex`,
    `align-items: center`,
    `gap: ${TOKEN.SPACING_SM}`,
    `box-sizing: border-box`,
    `border-left: ${rankBorderLeft(rank)}`,
    `box-shadow: ${rankBoxShadow(rank)}`,
    `overflow: hidden`,
  ].join('; ');

  // Avatar (36×36, circle)
  const avatar = document.createElement('div');
  avatar.setAttribute('data-scoreboard-avatar', '');
  avatar.style.cssText = [
    `width: 36px`,
    `height: 36px`,
    `border-radius: ${TOKEN.BORDER_RADIUS_CIRCLE}`,
    `background-color: ${playerColor}`,
    `flex-shrink: 0`,
    `display: flex`,
    `align-items: center`,
    `justify-content: center`,
    `overflow: hidden`,
    `font-family: ${TOKEN.FONT_BODY}`,
    `font-weight: ${TOKEN.FONT_WEIGHT_BOLD}`,
    `font-size: 16px`,
    `color: #FFFFFF`,
  ].join('; ');

  if (avatarUrl) {
    const img = document.createElement('img');
    img.src = avatarUrl;
    img.alt = playerName;
    img.style.cssText = 'width: 100%; height: 100%; object-fit: cover;';
    avatar.appendChild(img);
  } else {
    avatar.textContent = playerName.charAt(0).toUpperCase();
  }

  // Name span
  const nameSpan = document.createElement('span');
  nameSpan.setAttribute('data-scoreboard-name', '');
  nameSpan.textContent = playerName;
  nameSpan.style.cssText = [
    `font-family: ${TOKEN.FONT_BODY}`,
    `font-weight: ${TOKEN.FONT_WEIGHT_BOLD}`,
    `color: ${playerColor}`,
    `flex: 1`,
    `min-width: 0`,
    `overflow: hidden`,
    `text-overflow: ellipsis`,
    `white-space: nowrap`,
  ].join('; ');

  // HP Bar (via Plan 23a createHPBar)
  const hpBarHandle = createHPBar({
    gsapRegistry,
    currentHP,
    maxHP,
    height: 'scoreboard',
  });
  hpBarHandle.element.style.cssText += '; flex: 1.5; min-width: 48px;';

  // Keys span
  const keysSpan = document.createElement('span');
  keysSpan.setAttribute('data-scoreboard-keys', '');
  keysSpan.textContent = `🗝️${keys}`;
  keysSpan.style.cssText = [
    `font-family: ${TOKEN.FONT_BODY}`,
    `font-size: 14px`,
    `color: #FFD700`,
    `flex-shrink: 0`,
  ].join('; ');

  // Trophies span
  const trophiesSpan = document.createElement('span');
  trophiesSpan.setAttribute('data-scoreboard-trophies', '');
  trophiesSpan.textContent = `🏆${trophies}`;
  trophiesSpan.style.cssText = [
    `font-family: ${TOKEN.FONT_BODY}`,
    `font-size: 14px`,
    `color: #FFD700`,
    `flex-shrink: 0`,
  ].join('; ');

  // Assemble
  row.appendChild(avatar);
  row.appendChild(nameSpan);
  row.appendChild(hpBarHandle.element);
  row.appendChild(keysSpan);
  row.appendChild(trophiesSpan);

  // ── Mount ──────────────────────────────────────────────────────────────────
  if (mount) {
    mount.appendChild(row);
  }

  // ── Entrance animation (Plan 20a hook) ────────────────────────────────────
  const slideHook = createToastScoreboardSlideHook({ gsapRegistry });
  let slideHandle: GsapTimelineHandle | null = slideHook.play(row, {
    kind: 'toast-in',
  });

  // ── Mutator methods ────────────────────────────────────────────────────────

  function applyRankStyles(r: number): void {
    row.style.borderLeft = rankBorderLeft(r);
    row.style.boxShadow = rankBoxShadow(r);
  }

  function setRank(r: number): void {
    if (disposed) return;
    applyRankStyles(r);
  }

  function setHP(current: number, max: number): void {
    if (disposed) return;
    hpBarHandle.setHP(current, max);
  }

  function setKeys(n: number): void {
    if (disposed) return;
    keysSpan.textContent = `🗝️${n}`;
  }

  function setTrophies(n: number): void {
    if (disposed) return;
    trophiesSpan.textContent = `🏆${n}`;
  }

  function setEliminated(b: boolean): void {
    if (disposed) return;
    if (b) {
      row.style.opacity = '0.35';
      nameSpan.style.textDecoration = 'line-through';
    } else {
      row.style.opacity = '1';
      nameSpan.style.textDecoration = '';
    }
  }

  // ── dispose ────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Dispose HP bar
    hpBarHandle.dispose();

    // Dispose slide handle
    if (slideHandle) {
      slideHandle.dispose();
      slideHandle = null;
    }

    // Dispose the hook itself
    slideHook.dispose();

    // Remove from DOM
    if (row.parentNode) {
      row.parentNode.removeChild(row);
    }
  }

  return {
    element: row,
    setRank,
    setHP,
    setKeys,
    setTrophies,
    setEliminated,
    dispose,
  };
}
